import React from "react";
import { Link } from "react-router-dom";
import { Search, LayoutDashboard, TrendingUp, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SITE, siteApi, useLive, usePageMeta, money } from "@/lib/site";
import { PageHeader, Section, InfoCard, Prose, WaitlistForm } from "@/components/site/SiteBits";

const FEATURES = [
  { icon: Search, title: "Get discovered", body: "List in the right world and reach customers actively looking for what you offer." },
  { icon: LayoutDashboard, title: "Manage everything", body: "Bookings, calendar, clients, team seats, marketing and payouts in your provider dashboard." },
  { icon: TrendingUp, title: "Grow with data", body: "Analytics, price suggestions and review requests help you win repeat business." },
  { icon: QrCode, title: "Your Plus code", body: "Share your QR / Plus code so customers lock you into their Plus shelf and rebook in one tap." },
];

export function BusinessPage() {
  usePageMeta("For business", "List your business on Fable +: bookings, calendar, clients, staff, marketing and payouts in one dashboard. Free to start, plans from £34.70 a month with 0% commission.");
  return (
    <div>
      <PageHeader kicker="For business" title="Your whole business, in one dashboard" lede="Fable + gives local businesses a complete toolkit to get discovered, take bookings and get paid, without juggling five different apps." accent="var(--provider)">
        <div className="flex flex-wrap gap-3">
          <Link to="/partner/start"><Button className="rounded-full px-6" data-testid="business-start">Become a partner</Button></Link>
          <Link to="/pricing"><Button variant="outline" className="rounded-full px-6">See pricing</Button></Link>
        </div>
      </PageHeader>
      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "#F1E4FF", color: "var(--provider)" }}><Icon size={22} /></div>
                <h3 className="text-lg font-bold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
              </div>
            );
          })}
        </div>
      </Section>
      <Section alt>
        <div className="grid items-start gap-8 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--provider)" }}>Getting started</p>
            <h2 className="mt-2 font-serif-display text-3xl font-bold">Live in minutes</h2>
            <ol className="mt-5 space-y-3">
              {["Create your free account here or in the app.", "Confirm your email and the provider declaration.", "Publish your first listing with photos, prices and availability.", "Choose the plan that fits how you work, and share your Plus code."].map((s, i) => (
                <li key={s} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "var(--provider)" }}>{i + 1}</span>
                  <span className="pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <InfoCard title="Your responsibilities">
            <p>Every booking is a contract directly between you and your customer. You are responsible for what you offer and for holding any licences, registrations, checks and insurance your work needs, such as food hygiene registration, DBS checks or public liability cover.</p>
            <Link to="/legal/terms" className="mt-3 inline-block font-semibold text-primary hover:underline">Read the Terms →</Link>
          </InfoCard>
        </div>
      </Section>
      <Section title={SITE.hq.name + " for teams"} lede={SITE.hq.intro}>
        <Link to="/ops/pricing"><Button variant="outline" className="rounded-full px-6">Fable Ops pricing</Button></Link>
            <Link to="/business/enterprise"><Button variant="ghost" className="rounded-full px-6">Talk to us about Enterprise</Button></Link>
      </Section>
      <Section alt id="register">
        <div className="grid items-start gap-8 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--provider)" }}>Before launch</p>
            <h2 className="mt-2 font-serif-display text-3xl font-bold">Register your business</h2>
            <p className="mt-3 text-lg text-muted-foreground">Leave your details and we'll help you get set up before launch, so customers can find you from day one.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6"><WaitlistForm interest="provider" /></div>
        </div>
      </Section>
    </div>
  );
}

const TIER_ORDER = ["growth", "pro", "fleet", "scale", "enterprise", "payg"];

function detailFor(t) {
  const fixed = SITE.ladder.find((r) => r.name === t.tier_name);
  if (fixed) return fixed.detail;
  if (t.billing_model === "payg") return SITE.payg.detail;
  return "";
}

/** Live catalogue → cards; falls back to the exported copy when the API is unreachable. */
function ladderRows(cat) {
  const lane = cat?.partner || cat?.services_education;
  if (!lane) return { subs: SITE.ladder.map((r) => ({ name: r.name, price: money(r.price), detail: r.detail })), payg: { name: SITE.payg.name, price: money(SITE.payg.price), detail: SITE.payg.detail } };
  const rows = Object.entries(lane)
    .sort(([a], [b]) => TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b))
    .map(([key, t]) => ({ key, name: t.tier_name, price: money(t.monthly), detail: detailFor(t), payg: t.billing_model === "payg" }));
  return { subs: rows.filter((r) => !r.payg), payg: rows.find((r) => r.payg) };
}

function PlanCard({ r, highlight }) {
  return (
    <div className={`rounded-2xl border bg-card p-6 ${highlight ? "border-primary/40 ring-1 ring-primary/20" : "border-border"}`} data-testid={`plan-${r.key || r.name}`}>
      <h3 className="font-bold">{r.name}</h3>
      <p className="mt-3 font-serif-display text-3xl font-bold">{r.price}<span className="ml-1 font-sans text-sm font-medium text-muted-foreground">/mo</span></p>
      <p className="mt-3 text-sm text-muted-foreground">{r.detail}</p>
    </div>
  );
}

export function PricingPage() {
  usePageMeta("Pricing", "Fable + is free for customers. One partner plan for every world from £34.70 a month with 0% commission, or start free and pay 2.7% only when you earn.");
  const live = useLive(() => siteApi.plans());
  const rows = live === undefined ? undefined : ladderRows(live?.catalogue);
  return (
    <div>
      <PageHeader kicker="Pricing" title="Free for customers. Fair for business." lede="Customers never pay to browse, book or message. Partners pick one plan that covers every world, and switch worlds whenever they like, without a second subscription." />
      <Section title="One partner plan, every world" lede="Services, Education, Food, Casting, Experiences and Rentals. Subscription tiers are 0% commission on every booking.">
        {rows === undefined ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {rows.subs.map((r) => <PlanCard key={r.name} r={r} highlight={r.name === "Pro"} />)}
          </div>
        )}
      </Section>
      <Section alt title="Or pay as you go" lede="No monthly fee: you only pay when you earn.">
        <div className="grid gap-4 md:grid-cols-2">
          {rows === undefined ? <Skeleton className="h-40 rounded-2xl" /> : <PlanCard r={rows.payg} highlight />}
          <InfoCard title={SITE.rental_fee.name}>
            <p className="font-semibold text-foreground">{SITE.rental_fee.line}</p>
            <p className="mt-2">{SITE.rental_fee.detail}</p>
          </InfoCard>
        </div>
      </Section>
      <Section id="hq" title={SITE.hq.name} lede={`${SITE.hq.intro} Priced per seat.`}>
        <div className="grid gap-4 sm:grid-cols-3">
          {SITE.hq.rows.map((r) => <InfoCard key={r.name} title={r.name}>{r.value}</InfoCard>)}
        </div>
      </Section>
      <Section alt>
        <Prose>
          <h2 style={{ marginTop: 0 }}>The small print</h2>
          <ul>
            <li>Partner plans are business subscriptions billed by Fable + to your business, not in-app purchases.</li>
            <li>One plan covers every world: listing caps apply per world, so a Growth partner can have up to 5 listings in Services and another 5 in Food.</li>
            <li>Pro includes 3 staff, Max 10 and Scale 25; extra staff are £3.20 a month each. Extra worlds are £5.20 (Pro), £9.70 (Max) or £22.40 (Scale) a month each. Paying yearly is 10 months for 12.</li>
            <li>Months with no bookings are free on every monthly plan, and each month we show you whether pay as you go would have cost less.</li>
            <li>Customers pay one Booking fee on card bookings: payment processing (1.5% + 20p) plus a 1.4% Fable + service fee. Fable + Members pay processing only. Providers keep 100% of their price.</li>
            <li>Events: 6% + 50p per order, capped at £6.50, with card processing included. Organisers choose whether the buyer pays it or they absorb it. Your first event is free for its first 100 tickets.</li>
            <li>AI receptionist and texts: Scale includes 300 minutes and 500 texts a month, Scale+ 1,500 of each; Pro and Max can add 100 minutes and a number for £14.70/month. Top-ups: 100 minutes £9.70, 100 texts £5.70, 500 texts £24.70. Nothing is used without credit there first.</li>
            <li>Rentals carry a 5% owner rental-protection fee per digital booking on every plan.</li>
            <li>Change or cancel your plan any time in the provider dashboard under Plans &amp; pricing.</li>
            <li>Card payments are processed by Stripe. Business prices are shown before VAT.</li>
          </ul>
        </Prose>
      </Section>
    </div>
  );
}
